/**
 * Clipper Main App
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './store';
import useAuthValidation from './hooks/useAuthValidation';
import { Component } from 'react';
import '@/App.css';

// Error Boundary to catch crashes and offer localStorage reset
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  handleReset = () => {
    // Clear all localStorage
    localStorage.clear();
    sessionStorage.clear();
    // Reload the page
    window.location.href = '/auth';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-white mb-4">Algo salió mal</h1>
            <p className="text-gray-400 mb-6">
              Hubo un error al cargar la aplicación. Esto puede deberse a datos en caché corruptos.
            </p>
            <button
              onClick={this.handleReset}
              className="px-6 py-3 bg-[#BBFF00] text-black font-semibold rounded-lg hover:bg-[#a8e600] transition-colors"
            >
              Limpiar caché y reiniciar
            </button>
            <p className="text-xs text-gray-600 mt-4">
              Error: {this.state.error?.message || 'Desconocido'}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Pages
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Cancha from './pages/Cancha';
import Geolocation from './pages/Geolocation';
import QRScan from './pages/QRScan';
import ActionScreen from './pages/ActionScreen';
import Replays from './pages/Replays';
import Profile from './pages/Profile';
import SharedClip from './pages/SharedClip';
import ResetPassword from './pages/ResetPassword';
import VideoRedirect from './pages/VideoRedirect';
import WatchReplay from './pages/WatchReplay';
import VerifyEmail from './pages/VerifyEmail';

// Shop Pages
import Shop from './pages/shop/Shop';
import ShopSuccess from './pages/shop/ShopSuccess';
import ShopFailure from './pages/shop/ShopFailure';
import ShopPending from './pages/shop/ShopPending';

// Premium Pages
import PremiumUpgrade from './pages/premium/PremiumUpgrade';
import PremiumSuccess from './pages/premium/PremiumSuccess';
import PremiumFailure from './pages/premium/PremiumFailure';
import PremiumPending from './pages/premium/PremiumPending';

// Club Owner Pages
import ClubDashboard from './pages/club/ClubDashboard';
import ClubButtons from './pages/club/ClubButtons';
import ClubSettings from './pages/club/ClubSettings';
import ClubProfile from './pages/club/ClubProfile';
import ClubProfileEditor from './pages/club/ClubProfileEditor';
import ClubShop from './pages/club/ClubShop';
import ClubOrderHistory from './pages/club/ClubOrderHistory';
import ClubStats from './pages/club/ClubStats';
import ClubCameras from './pages/club/ClubCameras';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCameras from './pages/admin/AdminCameras';
import AdminCameraAssignments from './pages/admin/AdminCameraAssignments';
import AdminReservations from './pages/admin/AdminReservations';
import AdminClubs from './pages/admin/AdminClubs';
import AdminShelly from './pages/admin/AdminShelly';
import AdminShellyBlacklist from './pages/admin/AdminShellyBlacklist';
import AdminWatermarks from './pages/admin/AdminWatermarks';
import AdminSettings from './pages/admin/AdminSettings';
import AdminEcommerce from './pages/admin/AdminEcommerce';
import AdminFAQ from './pages/admin/AdminFAQ';
import AdminClips from './pages/admin/AdminClips';
import AdminSponsors from './pages/admin/AdminSponsors';

// POS Pages
import POSDashboard from './pages/pos/POSDashboard';
import POSProducts from './pages/pos/POSProducts';
import POSSales from './pages/pos/POSSales';
import POSCashSession from './pages/pos/POSCashSession';
import POSSettings from './pages/pos/POSSettings';
import POSUsers from './pages/pos/POSUsers';
import POSReports from './pages/pos/POSReports';

// Reservation Pages
import PublicReservation from './pages/reservations/PublicReservation';
import ReservationPaymentResult from './pages/reservations/ReservationPaymentResult';
import ClubReservations from './pages/club/ClubReservations';

// Playtomic Pages
import PlaytomicReservations from './pages/playtomic/PlaytomicReservations';

// FeedLoop
import FeedLoop from './pages/FeedLoop';

// Components
import FloatingReplayButton from './components/FloatingReplayButton';

// Protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false, requireClubOwner = false }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/home" replace />;
  }
  
  if (requireClubOwner && user?.role !== 'club_owner' && user?.role !== 'admin') {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// Route that redirects club owners and admins to their respective dashboards
const PlayerRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  // Club owners should go to club dashboard
  if (user?.role === 'club_owner') {
    return <Navigate to="/club" replace />;
  }
  
  // Admins should go to admin back office
  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Auth validation wrapper
const AuthValidator = ({ children }) => {
  useAuthValidation();
  return children;
};

function App() {
  return (
    <div className="App app-container">
      <BrowserRouter>
        <AuthValidator>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/share/:token" element={<SharedClip />} />
          <Route path="/v/:code" element={<VideoRedirect />} />
          
          {/* Smartwatch page - ultra simplified for Apple Watch / Wear OS */}
          <Route path="/watch" element={<WatchReplay />} />
          <Route path="/reloj" element={<WatchReplay />} />
          
          {/* Public reservation page */}
          <Route path="/reservar/:clubId" element={<PublicReservation />} />
          <Route path="/reservar/:clubId/:status/:reservationId" element={<ReservationPaymentResult />} />
          
          {/* Shop routes (public) */}
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/success" element={<ShopSuccess />} />
          <Route path="/shop/failure" element={<ShopFailure />} />
          <Route path="/shop/pending" element={<ShopPending />} />
          
          {/* Club Owner routes */}
          <Route
            path="/club"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/buttons"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubButtons />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/settings"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/profile"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/profile/edit"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubProfileEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/shop"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubShop />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/reservations"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubReservations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/playtomic"
            element={
              <ProtectedRoute requireClubOwner>
                <PlaytomicReservations isAdmin={false} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/orders"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubOrderHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/stats"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubStats />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/cameras"
            element={
              <ProtectedRoute requireClubOwner>
                <ClubCameras />
              </ProtectedRoute>
            }
          />
          
          {/* Protected routes - Player routes */}
          <Route
            path="/home"
            element={
              <PlayerRoute>
                <Dashboard />
              </PlayerRoute>
            }
          />
          <Route
            path="/cancha"
            element={
              <ProtectedRoute>
                <Cancha />
              </ProtectedRoute>
            }
          />
          <Route
            path="/geolocation"
            element={
              <ProtectedRoute>
                <Geolocation />
              </ProtectedRoute>
            }
          />
          <Route path="/scan" element={<QRScan />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <ActionScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/replays"
            element={
              <ProtectedRoute>
                <Replays />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          
          {/* FeedLoop - Accesible para todos los usuarios autenticados */}
          <Route
            path="/feedloop"
            element={
              <ProtectedRoute>
                <FeedLoop />
              </ProtectedRoute>
            }
          />
          
          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requireAdmin>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cameras"
            element={
              <ProtectedRoute requireAdmin>
                <AdminCameras />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cameras/assignments"
            element={
              <ProtectedRoute requireAdmin>
                <AdminCameraAssignments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clubs"
            element={
              <ProtectedRoute requireAdmin>
                <AdminClubs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shelly"
            element={
              <ProtectedRoute requireAdmin>
                <AdminShelly />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shelly/blacklist"
            element={
              <ProtectedRoute requireAdmin>
                <AdminShellyBlacklist />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/faq"
            element={
              <ProtectedRoute requireAdmin>
                <AdminFAQ />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/watermarks"
            element={
              <ProtectedRoute requireAdmin>
                <AdminWatermarks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requireAdmin>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ecommerce"
            element={
              <ProtectedRoute requireAdmin>
                <AdminEcommerce />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clips"
            element={
              <ProtectedRoute requireAdmin>
                <AdminClips />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sponsors"
            element={
              <ProtectedRoute requireAdmin>
                <AdminSponsors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/playtomic"
            element={
              <ProtectedRoute requireAdmin>
                <AdminReservations />
              </ProtectedRoute>
            }
          />
          
          {/* POS routes - Club owners and admin */}
          <Route
            path="/pos"
            element={
              <ProtectedRoute requireClubOwner>
                <POSDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos/products"
            element={
              <ProtectedRoute requireClubOwner>
                <POSProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos/sales"
            element={
              <ProtectedRoute requireClubOwner>
                <POSSales />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos/cash-session"
            element={
              <ProtectedRoute requireClubOwner>
                <POSCashSession />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos/settings"
            element={
              <ProtectedRoute requireClubOwner>
                <POSSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos/users"
            element={
              <ProtectedRoute requireClubOwner>
                <POSUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos/reports"
            element={
              <ProtectedRoute requireClubOwner>
                <POSReports />
              </ProtectedRoute>
            }
          />
          
          {/* Premium routes */}
          <Route
            path="/premium"
            element={
              <ProtectedRoute>
                <PremiumUpgrade />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premium/success"
            element={
              <ProtectedRoute>
                <PremiumSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premium/failure"
            element={
              <ProtectedRoute>
                <PremiumFailure />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premium/pending"
            element={
              <ProtectedRoute>
                <PremiumPending />
              </ProtectedRoute>
            }
          />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Floating Replay Button - visible when user has active session */}
        <FloatingReplayButton />
        </AuthValidator>
      </BrowserRouter>
      
      {/* Toast notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'hsl(240 6% 10%)',
            color: 'hsl(0 0% 98%)',
            border: '1px solid hsl(240 4% 16%)',
          },
        }}
      />
    </div>
  );
}

// Wrap App with ErrorBoundary
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
